/** Display rules for company lists and their companies. Pure; unit-tested. */
import type { CompanyListSummary } from "@nu-assist/contracts";

const SOURCE_LABEL: Record<CompanyListSummary["source"], string> = {
  chopbook: "ChopBook",
  csv_upload: "CSV import",
  derived: "Saved view",
  manual: "Manual",
};

/** How the list was born, as the "Made by" column says it. */
export function sourceLabel(source: CompanyListSummary["source"]): string {
  return SOURCE_LABEL[source];
}

/**
 * Money held in MILLIONS of USD (the API's unit), shown the way a recruiter
 * reads it: $476.1M, $1.2B, $850K. Null stays null so callers can hide the row.
 */
export function formatUsdMillions(value: number | null | undefined): string | null {
  if (value === null || value === undefined || Number.isNaN(value)) return null;
  if (Math.abs(value) >= 1000) return `$${trim(value / 1000)}B`;
  if (Math.abs(value) >= 1) return `$${trim(value)}M`;
  return `$${trim(value * 1000)}K`;
}

function trim(n: number): string {
  return n.toFixed(1).replace(/\.0$/, "");
}

export function formatCount(value: number | null | undefined): string | null {
  if (value === null || value === undefined) return null;
  return value.toLocaleString("en-US");
}

/** "Rome, Italy" / "Boston, MA, United States" -- whatever parts exist. */
export function formatHq(parts: ReadonlyArray<string | null | undefined>): string | null {
  const present = parts.map((p) => p?.trim()).filter((p): p is string => Boolean(p));
  return present.length > 0 ? present.join(", ") : null;
}

/** A website as a link target (PitchBook stores bare domains). */
export function websiteHref(url: string | null | undefined): string | null {
  if (!url) return null;
  return /^https?:\/\//i.test(url) ? url : `https://${url}`;
}

/**
 * A company's LinkedIn URL as a link target, or null. It comes from a CSV or
 * a PitchBook page, so only a LinkedIn web address is linked: anything else
 * (a `javascript:` URL in an imported file) is not rendered as a link.
 */
export function linkedinHref(url: string | null | undefined): string | null {
  const candidate = url?.trim();
  if (!candidate) return null;
  try {
    const u = new URL(/^https?:\/\//i.test(candidate) ? candidate : `https://${candidate}`);
    const hostOk = u.hostname === "linkedin.com" || u.hostname.endsWith(".linkedin.com");
    return u.protocol === "https:" || u.protocol === "http:" ? (hostOk ? u.href : null) : null;
  } catch {
    return null;
  }
}

/**
 * A company's PitchBook profile, from its PitchBook id ("41030-38"), or null.
 * The same URL PitchBook's own results page links each company name to. Only
 * an id of that shape is linked, so nothing else lands in the path.
 */
export function pitchbookHref(id: string | null | undefined): string | null {
  const candidate = id?.trim();
  if (!candidate || !/^\d+-\d+$/.test(candidate)) return null;
  return `https://my.pitchbook.com/profile/${candidate}/company/profile`;
}

/** A website without scheme or `www.`, for display. */
export function websiteLabel(url: string | null | undefined): string | null {
  if (!url) return null;
  return url
    .replace(/^https?:\/\//i, "")
    .replace(/^www\./i, "")
    .replace(/\/$/, "");
}

export function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}
